import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Store as StoreIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';

import { storeService } from '../../features/store-market/services/storeService';
import { StoreFormData } from '../../features/store-market/types/store.types';
// import { showNotification } from '../../store/slices/notificationSlice';

const StoreListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [stores, setStores] = useState<StoreFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStore, setSelectedStore] = useState<StoreFormData | null>(null);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await storeService.getUserStores();
      setStores(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar lojas:', error);
      alert(error.message || 'Erro ao carregar lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, store: StoreFormData) => {
    setAnchorEl(event.currentTarget);
    setSelectedStore(store);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStore(null);
  };

  const handleEdit = () => {
    if (selectedStore?.id) {
      navigate(`/dashboard/lojas/${selectedStore.id}/editar`);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedStore?.slug) {
      window.open(`/${selectedStore.slug}`, '_blank');
    }
    handleMenuClose();
  };

  const handleDashboard = () => {
    if (selectedStore?.id) {
      navigate(`/dashboard/lojas/${selectedStore.id}/dashboard`);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedStore?.id) return;

    if (window.confirm('Tem certeza que deseja excluir esta loja?')) {
      try {
        await storeService.deleteStore(selectedStore.id);
        alert('Loja excluída com sucesso!');
        loadStores();
      } catch (error: any) {
        console.error('Erro ao excluir loja:', error);
        alert(error.message || 'Erro ao excluir loja');
      }
    }
    handleMenuClose();
  };

  const handleCreateStore = () => {
    navigate('/dashboard/lojas/nova');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'paused':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'published':
        return 'Publicada';
      case 'draft':
        return 'Rascunho';
      case 'paused':
        return 'Pausada';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Carregando suas lojas...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            Minhas Lojas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gerencie suas lojas online
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateStore}
          size="large"
        >
          Nova Loja
        </Button>
      </Box>

      {/* Lista de lojas */}
      {stores.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <StoreIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Você ainda não tem nenhuma loja
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Crie sua primeira loja online e comece a vender seus produtos
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateStore}
          >
            Criar Primeira Loja
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {stores.map((store) => (
            <Grid item xs={12} md={6} lg={4} key={store.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header do card */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom noWrap>
                        {store.name}
                      </Typography>
                      <Chip
                        label={getStatusLabel(store.status)}
                        color={getStatusColor(store.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <IconButton
                      onClick={(e) => handleMenuClick(e, store)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Descrição */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {store.description}
                  </Typography>

                  {/* Informações da loja */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>URL:</strong> {store.slug}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Produtos:</strong> {store.products?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Categoria:</strong> {store.category}
                    </Typography>
                  </Box>

                  {/* Botões de ação */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => window.open(`/loja/${store.slug}`, '_blank')}
                      sx={{ flexGrow: 1 }}
                    >
                      Ver Loja
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DashboardIcon />}
                      onClick={() => navigate(`/dashboard/lojas/${store.id}/dashboard`)}
                      sx={{ flexGrow: 1 }}
                    >
                      Dashboard
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Menu de ações */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ViewIcon sx={{ mr: 1 }} />
          Ver Loja
        </MenuItem>
        <MenuItem onClick={handleDashboard}>
          <DashboardIcon sx={{ mr: 1 }} />
          Dashboard
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Excluir
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default StoreListPage;
